import React, { useState, useContext } from 'react';
import { X, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/providers/AuthProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type de feedback avec emojis
const FEEDBACK_TYPES = [
  { value: 'bug_critical', label: '🟥 Bug critique', emoji: '🟥' },
  { value: 'bug', label: '🟧 Bug', emoji: '🟧' },
  { value: 'ux', label: '🟨 UX', emoji: '🟨' },
  { value: 'suggestion', label: '🟦 Suggestion', emoji: '🟦' },
  { value: 'feature', label: '🟩 Fonctionnalité', emoji: '🟩' }
];

const FeedbackModal = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    title: '',
    description: ''
  });

  // Capture contexte automatique
  const captureContext = () => {
    return {
      browser_info: navigator.userAgent,
      page_url: window.location.pathname,
      app_version: '2.0.0', // TODO: Récupérer depuis env ou config
      screen_size: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || 'fr',
      workspace_id: localStorage.getItem('current_workspace') || null
    };
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Valider type et taille
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Format invalide. Utilisez PNG, JPEG ou WEBP.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      setScreenshot(file);
      toast.success('Capture d\'écran ajoutée');
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    // Reset file input
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.type) {
      toast.error('Veuillez sélectionner un type');
      return;
    }
    if (formData.title.length < 5) {
      toast.error('Le titre doit contenir au moins 5 caractères');
      return;
    }
    if (formData.description.length < 10) {
      toast.error('La description doit contenir au moins 10 caractères');
      return;
    }

    setLoading(true);

    try {
      // Préparer FormData avec contexte auto-capturé
      const submitData = new FormData();
      submitData.append('type', formData.type);
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);

      // Ajouter contexte auto-capturé
      const context = captureContext();
      submitData.append('browser_info', context.browser_info);
      submitData.append('page_url', context.page_url);
      submitData.append('app_version', context.app_version);
      submitData.append('screen_size', context.screen_size);
      submitData.append('language', context.language);
      if (context.workspace_id) {
        submitData.append('workspace_id', context.workspace_id);
      }

      // Ajouter screenshot si présent
      if (screenshot) {
        submitData.append('screenshot', screenshot);
      }

      // Envoyer au backend
      const response = await axios.post(`${API}/admin/beta/feedback`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      toast.success('Feedback envoyé avec succès ! Merci de votre contribution 🎉');

      // Reset form
      setFormData({ type: '', title: '', description: '' });
      setScreenshot(null);
      onOpenChange(false);

    } catch (error) {
      console.error('Feedback submission error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi du feedback';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">🐛 Centre de Feedback Beta</DialogTitle>
          <DialogDescription>
            Aidez-nous à améliorer NEXORA en partageant vos retours, bugs et suggestions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Type de feedback */}
          <div className="space-y-2">
            <Label htmlFor="feedback-type" className="text-sm font-medium">
              Type de feedback <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
            >
              <SelectTrigger id="feedback-type" className="w-full">
                <SelectValue placeholder="Sélectionnez un type..." />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Titre */}
          <div className="space-y-2">
            <Label htmlFor="feedback-title" className="text-sm font-medium">
              Titre <span className="text-red-500">*</span>
            </Label>
            <Input
              id="feedback-title"
              placeholder="Résumé court et descriptif..."
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={200}
              required
            />
            <p className="text-xs text-muted-foreground">
              {formData.title.length}/200 caractères
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="feedback-description" className="text-sm font-medium">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="feedback-description"
              placeholder="Décrivez votre feedback en détail..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              maxLength={5000}
              required
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {formData.description.length}/5000 caractères
            </p>
          </div>

          {/* Screenshot upload */}
          <div className="space-y-2">
            <Label htmlFor="screenshot-input" className="text-sm font-medium">
              Capture d'écran (optionnel)
            </Label>
            
            {!screenshot ? (
              <label
                htmlFor="screenshot-input"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Cliquez pour ajouter une capture
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPEG, WEBP (max 10MB)
                  </p>
                </div>
                <input
                  id="screenshot-input"
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleScreenshotChange}
                />
              </label>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">{screenshot.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({(screenshot.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveScreenshot}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Info auto-capture */}
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                <strong>Informations automatiquement collectées :</strong>
                <br />
                Navigateur, résolution d'écran, page actuelle, langue, workspace actif.
                <br />
                Ces données nous aident à reproduire et corriger les problèmes plus rapidement.
              </div>
            </div>
          </div>

          {/* Actions */}
          <DialogFooter className="flex space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Envoi...' : 'Envoyer le feedback'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
